// ============================================================
// OverdueDeadlinesCard.tsx — Round 2: the dashboard's OVERDUE section.
// Every open deadline past its due day that I'm responsible for (the same
// rule as the alerts: the case's team, else the firm's admins), however
// long ago — nothing drops off after a day. Danger-styled so it can't be
// missed; sits at the top of the dashboard and only renders when there's
// something overdue. Each row: Open case · Mark complete · Dismiss (not for
// limitation dates — those must be completed). Rows hide as soon as their
// action succeeds; the dashboard's live refresh re-syncs.
// Data: GET /api/case-deadlines/overdue (loaded with the rest of the
// dashboard, so it appears together with it — no late layout jump).
// ============================================================
import React, { useState } from 'react'
import { updateCaseDeadlineApi, dismissOverdueDeadlineApi } from '../../api/casevoApi'
import type { OverdueDeadline } from '../../types/deadlines.types'

const SHOW = 6
// 'Harrison v Bright Logistics · HARRISON-2026-4F9A2C' → name + mono ref.
const splitRef = (label: string): { name: string; ref: string | null } => {
  const at = label.lastIndexOf(' · ')
  return at > 0 ? { name: label.slice(0, at), ref: label.slice(at + 3) } : { name: label, ref: null }
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const civil = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}
const overdueBy = (n: number): string => `Overdue by ${n} day${n === 1 ? '' : 's'}`

const ROW_BTN = 'h-[28px] px-2.5 rounded-[8px] text-[12px] font-semibold border focus:outline-none casevo-focus disabled:opacity-50 whitespace-nowrap'

const OverdueDeadlinesCard: React.FC<{ rows: OverdueDeadline[] | null; onOpenCase: (caseId: string) => void }> = ({ rows, onOpenCase }) => {
  const [hidden, setHidden] = useState<Set<string>>(() => new Set())
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  const list = (rows ?? []).filter((r) => !hidden.has(r.id)).sort((a, b) => b.daysOverdue - a.daysOverdue)
  if (list.length === 0) return null
  const lim = list.filter((r) => r.isLimitation).length
  const visible = showAll ? list : list.slice(0, SHOW)

  const act = async (row: OverdueDeadline, fn: () => Promise<unknown>, failMsg: string): Promise<void> => {
    setBusyId(row.id)
    setError(null)
    try {
      await fn()
      setHidden((h) => new Set(h).add(row.id))
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : failMsg)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section aria-labelledby="overdue-deadlines-title"
      className="rounded-[14px] border border-[#f1c9c3] dark:border-[#6b3a35] border-l-[5px] border-l-[#b42318] dark:border-l-[#fca5a5] bg-casevo-surface shadow-[0_1px_2px_rgba(16,40,28,0.05),0_2px_6px_rgba(16,40,28,0.04)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)] px-5 sm:px-[26px] pt-5 pb-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="text-[#b42318] dark:text-[#fca5a5]" aria-hidden="true">
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" />
        </svg>
        <h2 id="overdue-deadlines-title" className="text-[11.5px] tracking-[0.14em] uppercase font-bold text-[#b42318] dark:text-[#fca5a5]">Overdue deadlines</h2>
        <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[12px] font-bold tabular-nums bg-[#fdecea] text-[#b42318] dark:bg-[#3a2422] dark:text-[#fca5a5]">{list.length}</span>
        {lim > 0 && <span className="text-[12px] font-semibold text-[#b42318] dark:text-[#fca5a5]">· {lim} limitation date{lim === 1 ? '' : 's'}</span>}
        <span className="basis-full text-[12.5px] text-casevo-text-soft">Past their due date and still open. They stay here until they’re marked complete or dismissed.</span>
      </div>
      <ul className="mt-3">
        {visible.map((r) => {
          const { name, ref } = splitRef(r.caseLabel)
          const busy = busyId === r.id
          return (
            <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2.5 border-t border-casevo-border-soft first:border-t-0">
              <div className="min-w-0 flex-1 basis-[240px]">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[14px] font-semibold text-casevo-forest truncate">{r.deadlineName}</span>
                  {r.isLimitation && <span className="shrink-0 text-[10.5px] font-bold uppercase tracking-[0.06em] px-1.5 py-px rounded bg-[#fdecea] text-[#b42318] dark:bg-[#3a2422] dark:text-[#fca5a5]">Limitation</span>}
                </div>
                <div className="text-[12.5px] text-casevo-text-soft truncate">
                  {name}{ref && <span className="ml-1.5 font-mono text-[11.5px]">{ref}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[13px] font-bold text-[#b42318] dark:text-[#fca5a5] tabular-nums">{overdueBy(r.daysOverdue)}</div>
                <div className="text-[11.5px] text-casevo-text-soft tabular-nums">was due {civil(r.dueDate)}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button type="button" onClick={() => onOpenCase(r.caseId)} className={`${ROW_BTN} border-casevo-border text-casevo-brand hover:border-casevo-mid`}>Open case</button>
                <button type="button" disabled={busy}
                  onClick={() => { void act(r, () => updateCaseDeadlineApi(r.id, { completed: true }), 'Couldn’t mark it complete.') }}
                  className={`${ROW_BTN} border-transparent bg-casevo-primary text-white hover:bg-casevo-primary-hover`}>
                  {busy ? 'Saving…' : 'Mark complete'}
                </button>
                {r.canDismiss
                  ? <button type="button" disabled={busy} title="Stop the overdue reminders without completing it (re-arms if the date changes)"
                      onClick={() => { void act(r, () => dismissOverdueDeadlineApi(r.id), 'Couldn’t dismiss it.') }}
                      className={`${ROW_BTN} border-casevo-border text-casevo-text-soft hover:text-casevo-forest`}>
                      Dismiss
                    </button>
                  : <span className="text-[11px] text-casevo-text-soft w-[62px] text-center leading-tight" title="Limitation dates can’t be dismissed — mark the deadline complete">Must be completed</span>}
              </div>
            </li>
          )
        })}
      </ul>
      {list.length > SHOW && (
        <button type="button" onClick={() => setShowAll((v) => !v)}
          className="mt-1 text-[12.5px] font-semibold text-casevo-brand hover:underline underline-offset-2 focus:outline-none casevo-focus">
          {showAll ? 'Show fewer' : `Show all ${list.length}`}
        </button>
      )}
      {error && <p role="alert" className="mt-2 text-[12.5px] text-casevo-danger">{error}</p>}
    </section>
  )
}

export default OverdueDeadlinesCard
